import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MenuItem, TreeNode } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { MainFacade } from '../../main.facade';
import { FilesService } from '../../../services/files.service';
import { FileInfoFactory } from '../../models/file-info/file-info.factory';
import { FileProject, IFile, IFileInfo, PatternType } from '../../models/file-info';
import {
  MetadataFilterDraft,
  emptyDraft,
  isDraftComplete,
  draftToFilter,
} from '../../models/metadata-filter';

@Component({
  selector: 'file-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SearchComponent implements OnInit, OnDestroy {
  // Exposed to template for instanceof pipe
  public readonly IFile = IFile;
  public readonly PatternType = PatternType;

  // ── Form fields ────────────────────────────────────────────
  searchPattern = '';
  patternType: PatternType = PatternType.WILDCARD;
  caseSensitive = false;
  selectInTree = false;

  // ── Sub-path breadcrumb ────────────────────────────────────────
  subPath = '';
  homeItem: MenuItem = {};
  breadcrumbItems: MenuItem[] = [];
  breadcrumbData: any[] = [];   // raw folder object parallel to breadcrumbItems
  overlayFolders: any[] = [];
  overlayLoading = false;
  pendingTruncateIndex: number | null = null; // null = home was clicked

  @ViewChild('subPathOverlay') subPathOverlay!: OverlayPanel;

  // ── Project / search state ───────────────────────────────────
  projects: FileProject[] = [];
  selectedProject: FileProject | null = null;
  searchResults: TreeNode<IFileInfo>[] = [];
  selectedResult: TreeNode<IFileInfo> | null = null;
  isSearching = false;

  searchSubscription: Subscription | null = null;

  // ── Metadata filters ───────────────────────────────────────────
  metadataFilters: MetadataFilterDraft[] = [];
  metadataExpanded = false;

  constructor(
    private facade: MainFacade,
    private filesService: FilesService
  ) {}

  ngOnInit(): void {
    this.homeItem = {
      icon: 'pi pi-home',
      tooltip: 'Project root',
      command: (event: any) => this.onHomeClick(event.originalEvent)
    };

    this.facade.getProjects$().subscribe(projects => {
      this.projects = projects;
    });
    this.facade.getSelectedProject$().subscribe(project => {
      this.selectedProject = project;
      if (project) {
        this.resetBreadcrumb();
      }
    });
  }

  // ── Project selector ─────────────────────────────────────────────

  loadingProjectsIcon(): string {
    return this.projects.length <= 0 ? 'pi pi-spin pi-spinner' : '';
  }

  onProjectSelect(event: any): void {
    this.facade.selectProject(event.value);
  }

  // ── Sub-path breadcrumb ────────────────────────────────────────

  private resetBreadcrumb(): void {
    this.breadcrumbItems = [];
    this.breadcrumbData = [];
    this.subPath = '';
  }

  onHomeClick(event: Event): void {
    if (!this.selectedProject) return;
    this.pendingTruncateIndex = null;
    this.loadAndShowOverlay(event, this.selectedProject.rawData);
  }

  onBreadcrumbItemClick(event: Event, index: number): void {
    this.pendingTruncateIndex = index;
    this.loadAndShowOverlay(event, this.breadcrumbData[index]);
  }

  private loadAndShowOverlay(event: Event, parentData: any): void {
    this.overlayFolders = [];
    this.overlayLoading = true;
    this.subPathOverlay.show(event);

    this.filesService.getChildren(parentData, 0, 200)
      .pipe(finalize(() => this.overlayLoading = false))
      .subscribe({
        next: (files: any[]) => {
          this.overlayFolders = files.filter((f: any) => f.type === 'Folder');
        },
        error: (err: HttpErrorResponse) => {
          this.overlayFolders = [];
          const msg = err?.error?.message ?? err?.message ?? 'Could not load folders';
          this.facade.errorMessage('Could not load folders', msg);
          console.error('getChildren failed', err);
        }
      });
  }

  onOverlayFolderSelect(folder: any): void {
    if (this.pendingTruncateIndex === null) {
      // Home was clicked — start from root
      this.breadcrumbData = [folder];
    } else {
      // Keep path up to and including the clicked item, append selected child
      this.breadcrumbData = [
        ...this.breadcrumbData.slice(0, this.pendingTruncateIndex + 1),
        folder
      ];
    }
    this.rebuildItems();
    this.subPath = folder.relPath ?? '';
    this.subPathOverlay.hide();
  }

  private rebuildItems(): void {
    this.breadcrumbItems = this.breadcrumbData.map((raw, i) => ({
      label: raw.name,
      command: (event: any) => this.onBreadcrumbItemClick(event.originalEvent, i)
    }));
  }

  // ── Metadata filters ───────────────────────────────────────────

  trackByIndex(index: number): number { return index; }

  addMetadataFilter(): void {
    this.metadataFilters = [...this.metadataFilters, emptyDraft()];
  }

  removeMetadataFilter(index: number): void {
    this.metadataFilters = this.metadataFilters.filter((_, i) => i !== index);
  }

  onFilterDraftChange(index: number, updated: MetadataFilterDraft): void {
    const copy = [...this.metadataFilters];
    copy[index] = updated;
    this.metadataFilters = copy;
  }

  get allFiltersComplete(): boolean {
    return this.metadataFilters.every(isDraftComplete);
  }

  // ── Search ───────────────────────────────────────────────────────────────

  canSearch(): boolean {
    return !!this.selectedProject
      && !!this.searchPattern.trim()
      && this.allFiltersComplete;
  }

  onSearch(): void {
    if (!this.canSearch()) return;

    this.cancelSearch();
    this.searchResults = [];
    this.isSearching = true;

    const criteria = this.metadataFilters.length > 0
      ? this.metadataFilters.map(d => draftToFilter(d, this.caseSensitive))
      : undefined;

    const request = {
      project:          this.selectedProject!.project,
      subPath:          this.subPath.trim() || undefined,
      pattern:          this.searchPattern.trim(),
      caseSensitive:    this.caseSensitive,
      patternType:      this.patternType,
      metadataCriteria: criteria,
    };

    this.searchSubscription = this.facade.searchFiles(request).subscribe({
      next: (rawFileInfo: any) => {
        const fileInfo = FileInfoFactory.getFileInfo(rawFileInfo);
        const nodes = this.filesService.createNodes([fileInfo]);
        if (nodes.length > 0) {
          this.searchResults = [...this.searchResults, nodes[0]];
        }
      },
      error: (err: any) => {
        console.error('Search error:', err);
        this.isSearching = false;
      },
      complete: () => {
        this.isSearching = false;
      }
    });
  }

  onCancel(): void {
    this.cancelSearch();
  }

  onResultSelect(node: TreeNode<IFileInfo>): void {
    this.selectedResult = node;
    this.facade.nodeSelect(node);

    if (this.selectInTree && node.data) {
      this.facade.navigateToFileInTree(node.data);
    }
  }

  private cancelSearch(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
      this.searchSubscription = null;
    }
    this.isSearching = false;
  }

  ngOnDestroy(): void {
    this.cancelSearch();
  }
}
